// Month view — 6×7 grid (Sunday-first), 3 event chips per cell with
// "+N more" overflow. Click a day jumps to Day view; click a chip
// opens event detail. Cells declare data-drop-day so the event-drag
// controller can move any event to a different date.

import { useMemo } from "preact/hooks";
import type { Calendar, CalendarEvent } from "../../lib/rpc";
import {
  CAL_DAYS, allDayRange, colorFor, dateKey, eventDayHours, fmtTime,
  monthCells, sameDay,
} from "../../lib/calendar-utils";
import type { EventDragController } from "../../lib/use-event-drag";
import { QueuedDot } from "../../components/queued-dot";

interface MonthViewProps {
  anchor: Date;
  today: Date;
  events: CalendarEvent[];
  calendars: Calendar[];
  onSelect: (evt: CalendarEvent) => void;
  onTapDay: (d: Date) => void;
  drag: EventDragController;
}

export function MonthView({ anchor, today, events, calendars, onSelect, onTapDay, drag }: MonthViewProps) {
  const cells = monthCells(anchor.getFullYear(), anchor.getMonth(), today);

  const byDate = useMemo(() => {
    const map = new Map<string, CalendarEvent[]>();
    for (const e of events) {
      if (e.allDay) {
        const r = allDayRange(e);
        if (!r) continue;
        const d = new Date(r.start);
        while (d <= r.end) {
          const k = dateKey(d);
          if (!map.has(k)) map.set(k, []);
          map.get(k)!.push(e);
          d.setDate(d.getDate() + 1);
        }
      } else {
        const k = dateKey(new Date(e.dtstart));
        if (!map.has(k)) map.set(k, []);
        map.get(k)!.push(e);
      }
    }
    return map;
  }, [events]);

  return (
    <div className="cal-month">
      <div className="cal-month-hd">
        {CAL_DAYS.map((d) => <div key={d}>{d}</div>)}
      </div>
      <div className="cal-month-grid">
        {cells.map((c, i) => {
          const items = c.inMonth ? (byDate.get(dateKey(c.date)) || []) : [];
          const visible = items.slice(0, 3);
          const overflow = items.length - visible.length;
          return (
            <div
              key={i}
              className="cal-month-cell"
              data-in={c.inMonth}
              data-today={c.isToday}
              data-drop-day={c.inMonth ? dateKey(c.date) : undefined}
              onClick={() => { if (c.inMonth) onTapDay(c.date); }}
            >
              <div className="cal-month-date">{c.inMonth ? c.date.getDate() : ""}</div>
              <div className="cal-month-items">
                {visible.map((e) => {
                  const hour = !e.allDay ? eventDayHours(e, c.date) : null;
                  return (
                    <button
                      key={e.id}
                      className="cal-month-chip"
                      data-dragging={drag.draggingId === e.id}
                      onPointerDown={drag.onPointerDown(e)}
                      style={{ ["--evt" as any]: `var(--${colorFor(e, calendars)})` }}
                      onClick={(ev) => { ev.stopPropagation(); onSelect(e); }}
                      type="button"
                    >
                      {hour && <span className="cal-month-time">{fmtTime(hour.start)}</span>}
                      <span className="cal-month-title">
                        {e.title}
                        <QueuedDot queued={e._queued} />
                      </span>
                    </button>
                  );
                })}
                {overflow > 0 && (
                  <div className="cal-month-more">+{overflow} more</div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export { sameDay };
